import React, { useMemo } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { DesignSystem, Plugin } from "../types/ThemeTypes";
import { useGoogleFonts } from "../hooks/useGoogleFonts";
import { useTenantFeConfig } from "@/contexts/TenantFeConfigContext";
import { getTenantContext } from "@/utils/tenant";
import Header from "@/components/renders/Header";
import Footer from "@/components/renders/Footer";
import layoutStyles from "@/styles/landing-layout.module.css";
import { PATHS } from "@/constants/paths";

export interface MainLayoutContext {
  tenantId?: string;
  landingPage: ReturnType<typeof useTenantFeConfig>["landingPage"];
  designSystem: DesignSystem;
  plugins: Plugin[];
  tenantDomain?: string;
  apiVersion: string;
}

const MainLayout: React.FC = () => {
  const location = useLocation();

  // Get tenant context from __TENANT_CONTEXT__ DOM element
  const { tenantId, tenantDomain } = useMemo(() => getTenantContext(), []);

  // Use tenant FE config from context
  const { landingPage, designSystem, plugins, isLoading, apiVersion } =
    useTenantFeConfig();

  // Load all fonts used in this component
  useGoogleFonts([
    designSystem?.primaryFont || "Poppins",
    designSystem?.secondaryFont || "Poppins",
    designSystem?.accentFont || "Poppins",
  ]);

  // Memoize background color
  const backgroundColor = useMemo(
    () => designSystem?.background || "#ffffff",
    [designSystem?.background]
  );

  // Show loading state
  if (isLoading) {
    return (
      <div
        style={{
          width: "100%",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <p>Loading...</p>
      </div>
    );
  }

  // Prepare context to pass to child routes
  const outletContext: MainLayoutContext = {
    tenantId,
    landingPage,
    designSystem,
    plugins,
    tenantDomain,
    apiVersion,
  };

  return (
    <div
      id="theme-root-container"
      className={layoutStyles.landing_page_container}
      style={{
        backgroundColor,
        height: "auto",
        minHeight: "100vh",
      }}
    >
      <div
        className={layoutStyles.landing_page_grid}
        style={{ backgroundColor }}
      >
        {/* Header Section */}
        {landingPage?.header && landingPage.header.length > 0 && (
          <Header
            blocks={landingPage.header}
            designSystem={designSystem || ({} as DesignSystem)}
            plugins={plugins}
            tenantDomain={tenantDomain}
            apiVersion={apiVersion}
          />
        )}

        {location.pathname === PATHS.LANDING ? (
          <div className={layoutStyles.landing_page_section_body}>
            <div className={layoutStyles.landing_page_content_box}>
              <Outlet context={outletContext} />
            </div>
          </div>
        ) : (
          <Outlet context={outletContext} />
        )}

        {location.pathname === PATHS.LANDING &&
          landingPage?.footer &&
          landingPage.footer.length > 0 && (
            <Footer
              blocks={landingPage.footer}
              designSystem={designSystem || ({} as DesignSystem)}
              plugins={plugins}
              tenantDomain={tenantDomain}
              apiVersion={apiVersion}
            />
          )}
      </div>
    </div>
  );
};

export default MainLayout;
